import React, { useContext, useEffect, useRef, useState } from 'react';
import { useInView } from '../../common/hooks/useInView';
import {
    Box,
    Button,
    Tabs,
    Tab,
    Typography,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from 'react-query';
import { ThemeContext } from 'styled-components';
import Authorized from '../../../layouts/Authorized';
import { CsrfContext } from '../../../providers/CsrfProvider';
import {
    getDefaultBillDetails,
    fetchDefaultBillStatementsPage,
    fetchDefaultBillTransactionsPage,
} from '../../../services/template/Templates';
import FormatDate from '../../../utils/formatters/FormatDate';
import FormatNumber from '../../../utils/formatters/FormatNumber';
import SectionHeader from '../components/SectionHeader';
import AddDefaultStatementRow from '../components/AddDefaultStatementRow';
import DefaultStatementRowMenu from '../components/DefaultStatementRowMenu';
import EditDefaultStatementRow from '../components/EditDefaultStatementRow';

// Shared table container styling to match FormStatements / TransactionsModel
const tableContainerSx = (theme) => ({
    mb: 0,
    pb: 0,
    mt: 0,
    pt: 0,
    mr: 0,
    ml: 0,
    width: '100%',
    borderRadius: 2,
    backgroundColor: theme.colors.tableBackground || 'white',
    border: 'none !important',
    borderBottom: 'none !important',
    borderTop: 'none !important',
    boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
    outline: 'none !important',
    overflow: 'hidden',
    '&::before': { display: 'none !important' },
    '&::after': { display: 'none !important' },
    '&.MuiPaper-root': {
        border: 'none !important',
        borderBottom: 'none !important',
        borderTop: 'none !important',
        boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
        marginBottom: '0 !important',
        paddingBottom: '0 !important',
        outline: 'none !important',
        borderRadius: 2,
        '&::before': { display: 'none !important' },
        '&::after': { display: 'none !important' },
    },
    '& .MuiTable-root': {
        border: 'none !important',
        borderBottom: 'none !important',
        borderTop: 'none !important',
        marginBottom: '0 !important',
        '& .MuiTableBody-root': {
            border: 'none !important',
            borderBottom: 'none !important',
            '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none !important',
            },
        },
    },
});

const sectionWrapperSx = {
    width: '100%',
    border: 'none',
    borderBottom: 'none',
    mb: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .MuiTableContainer-root': {
        borderBottom: 'none !important',
        '&::after': { display: 'none !important' },
    },
};

const throttle = (func, wait) => {
    let timeout;
    let previous = 0;
    return function (...args) {
        const now = Date.now();
        const remaining = wait - (now - previous);
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            func.apply(this, args);
        } else if (!timeout) {
            timeout = setTimeout(() => {
                previous = Date.now();
                timeout = null;
                func.apply(this, args);
            }, remaining);
        }
    };
};

const ViewDefaultBill = () => {
    const theme = useContext(ThemeContext);
    const mediaTheme = useTheme();
    const isExtraSmall = useMediaQuery(mediaTheme.breakpoints.down('sm'));
    const dateFormatLevel = isExtraSmall ? 0 : 1;
    const { csrfToken } = useContext(CsrfContext);
    const [activeTab, setActiveTab] = useState(0);
    const [editingStatementId, setEditingStatementId] = useState(null);
    const [accumulatedStatements, setAccumulatedStatements] = useState([]);
    const [accumulatedTransactions, setAccumulatedTransactions] = useState([]);
    const [nextStatementCursor, setNextStatementCursor] = useState('');
    const [nextTransactionCursor, setNextTransactionCursor] = useState('');
    const [loadingMoreStatements, setLoadingMoreStatements] = useState(false);
    const [loadingMoreTransactions, setLoadingMoreTransactions] = useState(false);
    const [scrollReady, setScrollReady] = useState(false);
    const scrollContainerRef = useRef(null);
    const sectionRefs = useRef([]);
    const tabsRef = useRef(null);

    const [statementsRefCallback, statementsInView] = useInView({
        rootRef: scrollContainerRef,
        rootReady: scrollReady,
    });
    const [transactionsRefCallback, transactionsInView] = useInView({
        rootRef: scrollContainerRef,
        rootReady: scrollReady,
    });
    const paneInView = statementsInView || transactionsInView;

    const { data, isLoading, isError, error } = useQuery(
        ['defaultBill'],
        () => getDefaultBillDetails(csrfToken),
        { enabled: !!csrfToken && paneInView }
    );

    const ownedStatementIds = data?.owned_statement_ids ?? [];

    useEffect(() => {
        if (data) {
            setAccumulatedStatements(data.statements ?? []);
            setAccumulatedTransactions(data.transactions ?? []);
            setNextStatementCursor(data.next_statement_cursor ?? '');
            setNextTransactionCursor(data.next_transaction_cursor ?? '');
        }
    }, [data]);

    const statements = accumulatedStatements;
    const transactions = accumulatedTransactions;
    const canShowMoreStatements = !!nextStatementCursor && !loadingMoreStatements;
    const canShowMoreTransactions = !!nextTransactionCursor && !loadingMoreTransactions;
    // Always show the button when there are items; enable when there's a next_cursor
    const showStatementsMoreButton = accumulatedStatements.length > 0;
    const showTransactionsMoreButton = accumulatedTransactions.length > 0;

    const handleShowMoreStatements = async () => {
        if (!csrfToken || !canShowMoreStatements) return;
        setLoadingMoreStatements(true);
        try {
            const { result, next_cursor } = await fetchDefaultBillStatementsPage(csrfToken, nextStatementCursor);
            setAccumulatedStatements((prev) => [...prev, ...result]);
            setNextStatementCursor(next_cursor ?? '');
        } finally {
            setLoadingMoreStatements(false);
        }
    };

    const handleShowMoreTransactions = async () => {
        if (!csrfToken || !canShowMoreTransactions) return;
        setLoadingMoreTransactions(true);
        try {
            const { result, next_cursor } = await fetchDefaultBillTransactionsPage(csrfToken, nextTransactionCursor);
            setAccumulatedTransactions((prev) => [...prev, ...result]);
            setNextTransactionCursor(next_cursor ?? '');
        } finally {
            setLoadingMoreTransactions(false);
        }
    };

    const sectionTitles = ['Statements', 'Transactions'];

    const scrollToSection = (index) => {
        const section = sectionRefs.current[index];
        if (section && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const topPos = section.offsetTop - container.offsetTop - 40;
            container.scrollTo({
                top: Math.max(0, topPos),
                left: 0,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        const handleScroll = throttle(() => {
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;

            const containerScrollPosition = scrollContainer.scrollTop;
            const viewportHeight = scrollContainer.clientHeight;
            const scrollCenter = containerScrollPosition + viewportHeight / 2;

            if (containerScrollPosition <= 10) {
                setActiveTab(0);
                return;
            }

            let bestMatch = null;
            sectionRefs.current.forEach((section, index) => {
                if (!section) return;
                const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;
                const visibleTop = Math.max(sectionTop, containerScrollPosition);
                const visibleBottom = Math.min(sectionBottom, containerScrollPosition + viewportHeight);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                const visibilityRatio = visibleHeight / section.offsetHeight;
                const isScrollCenterInSection = scrollCenter >= sectionTop && scrollCenter <= sectionBottom;

                if (isScrollCenterInSection) {
                    bestMatch = { index, priority: 2, visibilityRatio };
                } else if (visibilityRatio > 0.3 && (bestMatch === null || bestMatch.priority < 2)) {
                    if (bestMatch === null || visibilityRatio > bestMatch.visibilityRatio) {
                        bestMatch = { index, priority: 1, visibilityRatio };
                    }
                }
            });

            if (bestMatch) {
                setActiveTab(bestMatch.index);
            } else {
                let closestIndex = 0;
                let closestDistance = Infinity;
                sectionRefs.current.forEach((section, index) => {
                    if (!section) return;
                    const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                    const distance = Math.abs(sectionTop - containerScrollPosition);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });
                setActiveTab(closestIndex);
            }
        }, 100);

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            handleScroll();
        }
        return () => {
            if (scrollContainer) scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const centerSelectedTab = (tabIndex) => {
        if (!tabsRef.current) return;
        const tabButtons = tabsRef.current.querySelectorAll('.MuiTab-root');
        if (!tabButtons || tabButtons.length === 0) return;
        const selectedTab = tabButtons[tabIndex];
        if (selectedTab) {
            selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    if (paneInView && isLoading) {
        return (
            <Authorized>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        overflow: 'hidden',
                        backgroundColor: theme?.colors?.backgroundSecondary,
                    }}
                >
                    <Typography sx={{ color: theme?.colors?.text }}>Loading default bill...</Typography>
                </Box>
            </Authorized>
        );
    }

    if (isError) {
        return (
            <Authorized>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        overflow: 'hidden',
                        backgroundColor: theme?.colors?.backgroundSecondary,
                    }}
                >
                    <Typography color="error">
                        Failed to load default bill: {error?.message || 'Unknown error'}
                    </Typography>
                </Box>
            </Authorized>
        );
    }

    return (
        <Authorized>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    position: 'fixed',
                    overflow: 'hidden',
                    backgroundColor: theme.colors.backgroundSecondary,
                }}
            >
                <Box
                    sx={{
                        width: { xs: '100%', sm: '75%', md: 'min(80%, calc(100% - 280px))' },
                        maxWidth: { xs: '100%', sm: '800px', md: 'min(800px, calc(100% - 280px))' },
                        left: { xs: '50%', sm: '54.5%', md: '53%' },
                        transform: 'translateX(-50%)',
                        top: { xs: '5vw', sm: '5vw' },
                        bottom: { xs: '5vw', sm: '0' },
                        height: { xs: 'calc(85vh - 64px)', sm: '85vh' },
                        margin: 'auto',
                        paddingLeft: { xs: 0, sm: '10px' },
                        paddingRight: { xs: 0, sm: '10px' },
                        position: 'fixed',
                        boxShadow: 20,
                        borderRadius: { xs: 0, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.colors.backgroundPrimary,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    <SectionHeader title="Default Bill" />
                    <Tabs
                        ref={tabsRef}
                        value={activeTab}
                        onChange={(e, newValue) => {
                            setActiveTab(newValue);
                            scrollToSection(newValue);
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => centerSelectedTab(newValue));
                            });
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        indicatorColor="secondary"
                        textColor="inherit"
                        sx={{
                            '.MuiTabs-flexContainer': { justifyContent: 'space-between' },
                            '.MuiTab-root': {
                                flex: 1,
                                minWidth: '100px',
                                maxWidth: 'none',
                                padding: '6px 12px',
                                backgroundColor: 'transparent !important',
                                transition: 'opacity 120ms ease, background-color 0ms !important',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                    backgroundColor: 'transparent !important',
                                    transition: 'opacity 120ms ease, background-color 0ms !important',
                                },
                                '&.Mui-selected': {
                                    fontSize: '15px',
                                    fontWeight: 'bold',
                                    backgroundColor: 'transparent !important',
                                },
                                '& .MuiTouchRipple-root': { display: 'none !important' },
                                '& .MuiTouchRipple-ripple': { display: 'none !important' },
                            },
                        }}
                    >
                        {sectionTitles.map((title, index) => (
                            <Tab sx={{ color: theme.colors.text }} key={index} label={title} />
                        ))}
                    </Tabs>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            marginRight: { xs: 0, sm: '-5px' },
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            ref={(el) => {
                                scrollContainerRef.current = el;
                                if (el && !scrollReady) setScrollReady(true);
                            }}
                            sx={{
                                overflow: 'auto',
                                flex: 1,
                                minHeight: 0,
                                paddingRight: { xs: 0, sm: '5px' },
                                paddingTop: '40px',
                                boxSizing: 'border-box',
                                width: '100%',
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: `${theme.colors.brandSecondary} !important`,
                                },
                                scrollbarColor: `${theme.colors.brandSecondary} transparent`,
                            }}
                        >
                            <Box
                                ref={(el) => {
                                    sectionRefs.current[0] = el;
                                    statementsRefCallback(el);
                                }}
                                data-title="Statements"
                                sx={{
                                    paddingLeft: { xs: 0, sm: '10px' },
                                    paddingRight: { xs: 0, sm: '10px' },
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={sectionWrapperSx}>
                                    <TableContainer
                                        component={Box}
                                        elevation={0}
                                        sx={tableContainerSx(theme)}
                                    >
                                        <Table
                                            sx={{
                                                border: 'none !important',
                                                borderBottom: 'none !important',
                                                borderCollapse: 'separate',
                                                borderSpacing: 0,
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                    <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, pl: 0, fontSize: '1.75rem', py: 2 }}>
                                                        Statements
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }} />
                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {statements.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                        <TableCell colSpan={2} sx={{ color: theme.colors.text, fontStyle: 'italic', pl: 0, py: 2 }}>
                                                            No statements yet. Add one below.
                                                        </TableCell>
                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                    </TableRow>
                                                ) : (
                                                    <>
                                                        {[...statements]
                                                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                                            .map((s) => (
                                                                <React.Fragment key={s.id}>
                                                                    <TableRow
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                                        <TableCell sx={{ color: theme.colors.text, pl: 0, fontSize: '1.1rem' }}>
                                                                            {FormatDate(s.created_at, dateFormatLevel)}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 500, padding: '16px 0', fontSize: '1.1rem' }}>
                                                                            ${s.cost != null ? FormatNumber(s.cost) : '0'}
                                                                        </TableCell>
                                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                                                                            <DefaultStatementRowMenu
                                                                                statementId={s.id}
                                                                                ownedStatementIds={ownedStatementIds}
                                                                                onEdit={setEditingStatementId}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    {editingStatementId === s.id && (
                                                                        <EditDefaultStatementRow
                                                                            statement={s}
                                                                            onClose={() => setEditingStatementId(null)}
                                                                            onSuccess={() => setEditingStatementId(null)}
                                                                        />
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        {showStatementsMoreButton && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} sx={{ py: 1.5, borderBottom: 'none', verticalAlign: 'middle' }}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={!canShowMoreStatements}
                                                                        onClick={handleShowMoreStatements}
                                                                        sx={{
                                                                            color: theme?.colors?.brandSecondary,
                                                                            borderColor: theme?.colors?.brandSecondary,
                                                                            '&:hover': {
                                                                                borderColor: theme?.colors?.brandSecondary,
                                                                                backgroundColor: theme?.colors?.brandSecondary ? `${theme.colors.brandSecondary}14` : undefined,
                                                                            },
                                                                        }}
                                                                    >
{loadingMoreStatements ? 'Loading…' : 'Show more'}
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </>
                                                )}
                                                <AddDefaultStatementRow />
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                            <Box
                                ref={(el) => {
                                    sectionRefs.current[1] = el;
                                    transactionsRefCallback(el);
                                }}
                                data-title="Transactions"
                                sx={{
                                    paddingLeft: { xs: 0, sm: '10px' },
                                    paddingRight: { xs: 0, sm: '10px' },
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={sectionWrapperSx}>
                                    <TableContainer component={Box} sx={tableContainerSx(theme)}>
                                        <Table
                                            sx={{
                                                border: 'none !important',
                                                borderBottom: 'none !important',
                                                borderCollapse: 'collapse',
                                                borderSpacing: 0,
                                                width: '100%',
                                                tableLayout: { xs: 'fixed', sm: 'auto' },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                    <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, padding: '16px 16px 16px 0', fontSize: '1.75rem', py: 2 }}>
                                                        Transactions
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }} />
                                                    <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }} />
                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {transactions.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                        <TableCell colSpan={3} sx={{ color: theme.colors.text, fontStyle: 'italic', pl: 0, py: 2 }}>
                                                            No transactions yet.
                                                        </TableCell>
                                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                    </TableRow>
                                                ) : (
                                                    <>
                                                        {[...transactions]
                                                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                                            .map((t) => (
                                                                <TableRow
                                                                    key={t.id}
                                                                    sx={{
                                                                        '&:hover': {
                                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                                    <TableCell
                                                                        sx={{
                                                                            color: t.amount >= 0 ? (theme.colors.success || '#4caf50') : (theme.colors.error || '#f44336'),
                                                                            fontWeight: 500,
                                                                            padding: { xs: '16px 2px 16px 0', sm: '16px 16px 16px 0' },
                                                                            fontSize: '1.1rem',
                                                                            width: { xs: '60px', sm: 'auto' },
                                                                            maxWidth: { xs: '60px', sm: '120px' },
                                                                            minWidth: { xs: '60px', sm: 'auto' },
                                                                        }}
                                                                    >
                                                                        {t.amount != null
                                                                            ? (t.amount >= 0 ? '$' : '-$') + FormatNumber(Math.abs(t.amount))
                                                                            : '0.00'}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: theme.colors.text,
                                                                            pl: { xs: 0, sm: 0 },
                                                                            pr: { xs: 2, sm: 0 },
                                                                            fontSize: '1.1rem',
                                                                            width: { xs: 'auto', sm: 'auto' },
                                                                            minWidth: { xs: '100px', sm: 'auto' },
                                                                        }}
                                                                    >
                                                                        {FormatDate(t.created_at, dateFormatLevel)}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        align="right"
                                                                        sx={{
                                                                            color: theme.colors.text,
                                                                            padding: '16px 0',
                                                                            fontSize: '1.1rem',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            width: { xs: 'auto', sm: 'auto' },
                                                                            minWidth: { xs: '100px', sm: '150px' },
                                                                            maxWidth: { xs: '40%', sm: 'none' },
                                                                        }}
                                                                    >
                                                                        {t.user?.email || t.user?.sub || '—'}
                                                                    </TableCell>
                                                                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                                                                </TableRow>
                                                            ))}
                                                        {showTransactionsMoreButton && (
                                                            <TableRow>
                                                                <TableCell colSpan={5} sx={{ py: 1.5, borderBottom: 'none', verticalAlign: 'middle' }}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={!canShowMoreTransactions}
                                                                        onClick={handleShowMoreTransactions}
                                                                        sx={{
                                                                            color: theme?.colors?.brandSecondary,
                                                                            borderColor: theme?.colors?.brandSecondary,
                                                                            '&:hover': {
                                                                                borderColor: theme?.colors?.brandSecondary,
                                                                                backgroundColor: theme?.colors?.brandSecondary ? `${theme.colors.brandSecondary}14` : undefined,
                                                                            },
                                                                        }}
                                                                    >
                                                                        {loadingMoreTransactions ? 'Loading…' : 'Show more'}
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Authorized>
    );
};

export default ViewDefaultBill;
